import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { theme } from '../config/theme';

const TermsAndConditions: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <Link 
          to="/home"
          className="inline-flex items-center space-x-2 text-sm mb-8 transition-colors hover:text-blue-600"
          style={{ color: theme.colors.primary[600] }}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back to Home</span>
        </Link>

        {/* Terms and Conditions Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.primary[900] }}>
            Terms and Conditions
          </h1>
          <h2 className="text-xl mb-8" style={{ color: theme.colors.primary[700] }}>
            Bounce Dads ATL (LLH Party Rentals) Bounce House Rental Terms and Conditions
          </h2>
          
          <p className="text-sm text-gray-600 mb-8">
            Effective Date: November 12, 2025
          </p>

          <div className="prose prose-sm max-w-none" style={{ color: theme.colors.text.primary }}>
            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              1. Rental Agreement Overview
            </h2>
            <p className="mb-6">
              This Rental Agreement ("Agreement") is entered into by and between the Renter ("Customer") and Bounce Dads ATL (LLH Party Rentals) ("Company") for the rental of inflatable bounce house equipment. By agreeing to this agreement, Customer agrees to abide by all terms and conditions herein.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              2. Rental Period
            </h2>
            <p className="mb-6">
              The rental period listed at checkout is the agreed upon rental day(s). Any extension of the rental period must be approved by the Company and may incur additional charges.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              3. Delivery and Setup
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Company will deliver, set up, and take down the equipment.</li>
              <li>Customer must ensure clear access to the setup location.</li>
              <li>Equipment must be set up on a flat, clean, and grassy (preferred) or smooth surface free of debris, sharp objects, or animal waste.</li>
              <li>Electrical outlet must be available within 100 feet of set up location.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              4. Payment Terms
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Reservation deposit (15%) is due at checkout</li>
              <li>Remaining payment is due before or at the time of delivery via invoice.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              5. Cancellation Policy
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Cancellations requests received within 48 hours (with event further than 48 hours out) will receive a full refund.</li>
              <li>Cancellations after 48 hours of booking will receive a refund as credit for future use. Credit is good for up to 1 year.</li>
              <li>In case of inclement weather (e.g., high winds, heavy rain), the Company may cancel the rental at its discretion for safety reasons, and a full refund will be provided.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              6. Safety Rules
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Adult supervision is required at all times for children less than 18.</li>
              <li>No shoes, food, drinks, sharp objects, pets, or rough play inside the bounce house.</li>
              <li>Do not exceed the maximum number of users or weight limit posted on the unit.</li>
              <li>Users must not climb or hang on the walls or netting.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              7. Customer Responsibilities
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Ensure the bounce house remains clean and free of debris.</li>
              <li>Keep the blower and power cords dry and protected.</li>
              <li>Notify the Company immediately of any damage or malfunction.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              8. Damage and Liability
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Customer is responsible for any damage (other than normal wear and tear) caused by misuse, negligence, or violation of these terms.</li>
              <li>Customer assumes full responsibility for all persons using the equipment.</li>
              <li>Company is not liable for any injury or damages arising from improper use or lack of supervision.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              9. Weather Policy
            </h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Equipment must not be used in winds over 15 mph, rain, or lightning.</li>
              <li>If weather conditions change during the rental, Customer must immediately deflate and stop use.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              10. Indemnification
            </h2>
            <p className="mb-6">
              Customer agrees to indemnify and hold harmless Bounce Dads ATL (LLH Party Rentals), its owners, agents, and employees from any and all claims, actions, suits, damages, liabilities, and expenses arising from or related to the use of the rental equipment.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              11. Governing Law
            </h2>
            <p className="mb-6">
              This agreement shall be governed by and construed in accordance with the laws of the State of Georgia.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              12. Entire Agreement
            </h2>
            <p className="mb-6">
              This document constitutes the entire agreement between the parties. No oral modifications or representations are valid unless in writing and signed by both parties.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              Contact Information
            </h2>
            <div className="pl-6 mb-6 space-y-2">
              <p><strong>Bounce Dads ATL</strong></p>
              <p>Email: support@bouncedadsatl.com</p>
              <p>Phone: (404) 955-8126</p>
              <p>Location: Atlanta, Georgia</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
